import { MetaOf } from "react-async-stateful";

import * as Flags from "../../lib/Flags";
import * as Api from "../../Api";
import * as PillarDataUtils from "../../lib/util/PillarDataUtils";
import * as UserSelectors from "../User/User.selector";
import * as DashboardSelectors from "../../store/Dashboard/DashboardSelectors";

import dayjs from "../../lib/vendor/DayJs";
import { actionCreator, asyncActionCreators } from "../../lib/util/ReduxUtils";
import { ScopeInfo } from "../../lib/types/MiscTypes";
import { JourneyAggregate } from "../../lib/types/JourneyTypes";
import { FormConfig, Report, ReportResponse } from "../../lib/types/Reports";
import { FleetStats, FleetUserCounts } from "../../lib/types/Fleet";
import { DateRange } from "../../lib/types/DateTimeTypes";
import { selectCurrentScopeUserCount } from "../../store/Dashboard/DashboardSelectors";
import { PillarBoxplotsData } from "../../components/custom/PillarBoxplot/PillarBoxplot";
import { DashboardReducerState } from "./DashboardReducer";
import { DEFAULT_CACHE_EXPIRE } from "../../lib/api/middleware/CacheMiddleware";

// get data
export const fetchRootJourneys = asyncActionCreators<
	{
		bmOnlyUsers?: boolean;
		newTimeRange?: string;
		fleetId: string;
		group?: string | null;
	},
	JourneyAggregate[]
>("[DASHBOARD] FETCH_ROOT_JOURNEYS", async (action, dispatch, getState) => {
	const state = getState();

	const { newTimeRange, fleetId, bmOnlyUsers } = action.payload;

	const timeSelectionId =
		newTimeRange ||
		DashboardSelectors.getDashboardPillarSummaryTimeSelectionId(state);
	const timeSelection = PillarDataUtils.getTimeSelectionById(timeSelectionId);

	const grouping = timeSelection.grouping.unit;

	const { from, to } = PillarDataUtils.getTimeSpan(timeSelectionId);
	const rootGroup = UserSelectors.selectCurrentScopeRootGroup(state);

	const appType = UserSelectors.selectCurrentScopeAppType(state);
	const vendor = UserSelectors.selectCurrentScopeVendor(state);

	const res = await Api.fetchFleetGroupScores.submit({
		params: { fleetId },
		query: {
			from,
			to,
			groupBy: grouping,
			group: rootGroup?.id || undefined,
			appType: appType || "software",
			vendor: vendor || "brightmile",
			bmOnlyUsers,
		},
		options: { lock: "fleet_journey_stats" },
	});

	return res.data;
});

const addFormStatus = (report: Report, formConfig: FormConfig): Report => {
	if (!report.formData) {
		report.status = "✖️";
		return report;
	}

	const formData = JSON.parse(report.formData);
	const formDataFinal = [...formData];
	let numFailed = 0;

	formData.forEach((formItem: any, i: number) => {
		formDataFinal[i].error = false;
		formConfig.failStates?.forEach((failState) => {
			if (formItem.type === failState.type) {
				if (formItem.value === failState.value) {
					numFailed++;
					formDataFinal[i].error = true;
				}
			}
		});
	});

	report.formData = JSON.stringify(formDataFinal);

	if (numFailed > 0) {
		report.status = "❗";
	} else {
		report.status = "✅";
	}

	return report;
};

export const fetchRootReports = asyncActionCreators<
	{
		newDateRange?: any;
		newTimeRange?: string;
		fleetId: string;
		group?: string | null | undefined;
		hasRootAccess?: boolean;
		formId?: string;
		groupIds?: string[];
	},
	Report[]
	// @ts-ignore
>("[DASHBOARD] FETCH_ROOT_REPORTS", async (action, dispatch, getState) => {
	const state = getState();
	const { newDateRange, fleetId, formId, groupIds, group, hasRootAccess } =
		action.payload;

	const from = newDateRange?.from
		? dayjs(newDateRange?.from).startOf("day").add(12, "hours").valueOf()
		: dayjs()
				.day(1)
				.subtract(7, "day")
				.startOf("day")
				.add(12, "hours")
				.valueOf() + 1;
	const to = newDateRange?.to
		? dayjs(newDateRange?.to).endOf("day").subtract(12, "hours").valueOf()
		: dayjs()
				.day(1)
				.add(6, "day")
				.endOf("day")
				.subtract(12, "hours")
				.valueOf() - 1;

	const res = await Api.fetchFleetReports.submit({
		params: {
			fleetId: fleetId,
		},
		query: {
			from,
			to,
			timeRange: "day",
			formId,
			groupIds,
		},
		options: {
			lock: "fleet_reports",
		},
	});

	const results = res.data.results;
	const returnResults = results.map((report: Report) => ({
		...addFormStatus(report, res.data.formConfig),
	}));
	return returnResults;
});

export const fetchComplianceReports = asyncActionCreators<
	{
		newDateRange?: any;
		newTimeRange?: string;
		fleetId: string;
		group?: string | null | undefined;
		hasRootAccess?: boolean;
		formId?: string;
		userId?: string;
		groupIds?: string[];
	},
	any[]
>(
	"[DASHBOARD] FETCH_COMPLIANCE_REPORTS",
	// @ts-ignore
	async (action, dispatch, getState) => {
		const state = getState();
		const {
			newDateRange,
			fleetId,
			formId,
			groupIds,
			group,
			hasRootAccess,
			userId,
		} = action.payload;

		const from = newDateRange?.from
			? dayjs(newDateRange?.from).startOf("day").add(12, "hours").valueOf()
			: dayjs()
					.day(1)
					.subtract(7, "day")
					.startOf("day")
					.add(12, "hours")
					.valueOf() + 1;
		const to = newDateRange?.to
			? dayjs(newDateRange?.to).endOf("day").subtract(12, "hours").valueOf()
			: dayjs()
					.day(1)
					.add(6, "day")
					.endOf("day")
					.subtract(12, "hours")
					.valueOf() - 1;

		const res = await Api.fetchComplianceReports.submit({
			params: {
				fleetId,
			},
			query: {
				from,
				to,
				group,
				timeRange: "day",
				formId,
				groupIds,
			},
			options: {
				lock: "compliance_reports",
			},
		});

		return res.data;
	},
);

export const fetchComplianceReport = asyncActionCreators<
	{
		newDateRange?: any;
		newTimeRange?: string;
		fleetId: string;
		group?: string | null | undefined;
		userId?: string | null | undefined;
		hasRootAccess?: boolean;
		formId?: string;
		groupIds?: string[];
	},
	any[]
>("[DASHBOARD] FETCH_COMPLIANCE_REPORT", async (action, dispatch, getState) => {
	const state = getState();
	const { newDateRange, fleetId, formId, groupIds, group, hasRootAccess } =
		action.payload;

	const from = newDateRange?.from
		? dayjs(newDateRange?.from).startOf("day").add(12, "hours").valueOf()
		: dayjs()
				.day(1)
				.subtract(7, "day")
				.startOf("day")
				.add(12, "hours")
				.valueOf() + 1;
	const to = newDateRange?.to
		? dayjs(newDateRange?.to).endOf("day").subtract(12, "hours").valueOf()
		: dayjs()
				.day(1)
				.add(6, "day")
				.endOf("day")
				.subtract(12, "hours")
				.valueOf() - 1;

	const res = await Api.fetchComplianceReport.submit({
		params: {
			fleetId,
			formId,
		},
		query: {
			from,
			to,
			group,
			timeRange: "day",
			groupIds,
		},
		options: {
			lock: "compliance_report",
		},
	});

	return res.data.results;
});

export const fetchGroupRankingReport = asyncActionCreators<
	{
		newDateRange?: any;
		newTimeRange?: string;
		period?: string;
		fleetId: string;
		group?: string | null | undefined;
		hasRootAccess?: boolean;
		formId?: string;
		groupIds?: string[];
	},
	any
>(
	"[DASHBOARD] FETCH_GROUP_RANKING_REPORT",
	async (action, dispatch, getState) => {
		const state = getState();
		const { newDateRange, fleetId, groupIds, period, group, hasRootAccess } =
			action.payload;

		const from = newDateRange?.from
			? dayjs(newDateRange?.from).startOf("day").add(12, "hours").valueOf() + 1
			: dayjs()
					.day(1)
					.startOf("day")
					.subtract(7, "day")
					.add(12, "hours")
					.valueOf() + 1;
		const to = newDateRange?.to
			? dayjs(newDateRange?.to).endOf("day").subtract(12, "hours").valueOf() - 1
			: dayjs()
					.day(1)
					.endOf("day")
					.add(7, "day")
					.subtract(12, "hours")
					.valueOf() - 1;

		const res = await Api.fetchGroupRankingReport.submit({
			params: {
				fleetId: fleetId,
			},
			query: {
				from,
				to,
				period,
				groupIds,
				groupBy: period,
			},
			options: {
				lock: "group_ranking_report",
			},
		});

		const returnResults = res.data;
		return returnResults;
	},
);

export const fetchUserRankingReport = asyncActionCreators<
	{
		newDateRange?: any;
		newTimeRange?: string;
		period?: string;
		fleetId: string;
		group?: string | null | undefined;
		hasRootAccess?: boolean;
		formId?: string;
		groupIds?: string[];
	},
	any
>(
	"[DASHBOARD] FETCH_USER_RANKING_REPORT",
	async (action, dispatch, getState) => {
		const state = getState();
		const { newDateRange, fleetId, groupIds, period, group, hasRootAccess } =
			action.payload;

		const from = newDateRange?.from
			? dayjs(newDateRange?.from).startOf("day").add(12, "hours").valueOf() + 1
			: dayjs()
					.day(1)
					.startOf("day")
					.subtract(7, "day")
					.add(12, "hours")
					.valueOf() + 1;
		const to = newDateRange?.to
			? dayjs(newDateRange?.to).endOf("day").subtract(12, "hours").valueOf() - 1
			: dayjs()
					.day(1)
					.endOf("day")
					.add(7, "day")
					.subtract(12, "hours")
					.valueOf() - 1;

		const res = await Api.fetchUserRankingReport.submit({
			params: {
				fleetId: fleetId,
			},
			query: {
				from,
				to,
				period,
				groupBy: period,
				groupIds,
			},
			options: {
				lock: "user_ranking_report",
			},
		});

		const returnResults = res.data;
		return returnResults;
	},
);

export const fetchBrightCoachGroupRankingReport = asyncActionCreators<
	{
		newDateRange?: any;
		newTimeRange?: string;
		period?: string;
		fleetId: string;
		group?: string | null | undefined;
		hasRootAccess?: boolean;
		formId?: string;
		groupIds?: string[];
	},
	any
>(
	"[DASHBOARD] FETCH_BRIGHTCOACH_GROUP_RANKING_REPORT",
	async (action, dispatch, getState) => {
		const state = getState();
		const { newDateRange, fleetId, groupIds, period, group, hasRootAccess } =
			action.payload;

		const from = newDateRange?.from
			? dayjs(newDateRange?.from).startOf("day").add(12, "hours").valueOf() + 1
			: dayjs()
					.day(1)
					.startOf("day")
					.subtract(7, "day")
					.add(12, "hours")
					.valueOf() + 1;
		const to = newDateRange?.to
			? dayjs(newDateRange?.to).endOf("day").subtract(12, "hours").valueOf() - 1
			: dayjs()
					.day(1)
					.endOf("day")
					.add(7, "day")
					.subtract(12, "hours")
					.valueOf() - 1;

		const res = await Api.fetchBrightCoachGroupRankingReport.submit({
			params: {
				fleetId: fleetId,
			},
			query: {
				from,
				to,
				period,
				groupIds,
				groupBy: period,
			},
			options: {
				lock: "brightcoach_group_ranking_report",
			},
		});

		const returnResults = res.data.results;
		return returnResults;
	},
);

export const fetchBrightCoachUserRankingReport = asyncActionCreators<
	{
		newDateRange?: any;
		newTimeRange?: string;
		period?: string;
		fleetId: string;
		group?: string | null | undefined;
		hasRootAccess?: boolean;
		formId?: string;
		groupIds?: string[];
	},
	any
>(
	"[DASHBOARD] FETCH_BRIGHTCOACH_USER_RANKING_REPORT",
	async (action, dispatch, getState) => {
		const state = getState();
		const { newDateRange, fleetId, groupIds, period, group, hasRootAccess } =
			action.payload;

		const from = newDateRange?.from
			? dayjs(newDateRange?.from).startOf("day").add(12, "hours").valueOf() + 1
			: dayjs()
					.day(1)
					.startOf("day")
					.subtract(7, "day")
					.add(12, "hours")
					.valueOf() + 1;
		const to = newDateRange?.to
			? dayjs(newDateRange?.to).endOf("day").subtract(12, "hours").valueOf() - 1
			: dayjs()
					.day(1)
					.endOf("day")
					.add(7, "day")
					.subtract(12, "hours")
					.valueOf() - 1;

		const res = await Api.fetchBrightCoachUserRankingReport.submit({
			params: {
				fleetId: fleetId,
			},
			query: {
				from,
				to,
				period,
				groupBy: period,
				groupIds,
			},
			options: {
				lock: "brightcoach_user_ranking_report",
			},
		});

		const returnResults = res.data.results;
		return returnResults;
	},
);

export const fetchFleetOrGroupStats = asyncActionCreators<
	ScopeInfo & { appType?: string; vendor?: string },
	FleetStats
>(
	"[DASHBOARD] FETCH_FLEET_OR_GROUP_STATS",
	async (action, dispatch, getState) => {
		const { fleetId, group } = action.payload;
		dispatch(fetchUserCounts.submit(action.payload));

		const res = await Api.fetchFleetStats.submit({
			params: { fleetId: fleetId },
			options: { lock: "fleet_stats" },
			query: {
				group: group || undefined,
			},
		});

		return res.data;
	},
);

export const fetchPillarBoxplots = asyncActionCreators<
	{ scope: ScopeInfo; appType: string; vendor: string },
	PillarBoxplotsData
>("[DASHBOARD] FETCH_PILLAR_BOXPLOTS", async (action, dispatch, getState) => {
	const { scope, appType, vendor } = action.payload;
	const { fleetId, group } = scope;

	const res = await Api.fetchFleetViewPillarBoxplots.submit({
		params: { fleetId: fleetId },
		query: {
			group: group || undefined,
			ostrichMode: Flags.FORCE_OSTRICH_MODE.get() === true || undefined,
			appType: appType || "software",
			vendor: vendor,
		},
	});
	return res.data;
});

export const fetchGroupJourneys = asyncActionCreators<
	ScopeInfo & { newTimeRange?: string; bmOnlyUsers?: boolean },
	JourneyAggregate[]
>("[DASHBOARD] FETCH_GROUP_JOURNEYS", async (action, dispatch, getState) => {
	const state = getState();
	const { bmOnlyUsers, fleetId, group, newTimeRange, rootGroupId } =
		action.payload;

	if (!group || group === rootGroupId) {
		return [];
	}

	const timeSelectionId =
		newTimeRange ||
		DashboardSelectors.getDashboardPillarSummaryTimeSelectionId(getState());
	const timeSelection = PillarDataUtils.getTimeSelectionById(timeSelectionId);

	const appType = UserSelectors.selectCurrentScopeAppType(state);
	const vendor = UserSelectors.selectCurrentScopeVendor(state);

	const grouping = timeSelection.grouping.unit;

	const { from, to } = PillarDataUtils.getTimeSpan(timeSelectionId);

	const result = await Api.fetchFleetGroupScores.submit({
		params: { fleetId },
		query: {
			from,
			to,
			group,
			groupBy: grouping,
			appType: appType || "software",
			vendor: vendor || "brightmile",
			bmOnlyUsers,
		},
		options: { lock: "group_journeys" },
	});

	return result.data;
});

export const toggleHwScoreFilter = actionCreator<any>(
	"[DASHBOARD] TOGGLE_HW_SCORE_FILTER",
	(action, dispatch, getState) => {
		const state = getState();
		const scope = UserSelectors.selectCurrentScope(state);

		const bmOnlyUsers = action.payload?.value === "Brightmile";
		dispatch(
			fetchRootJourneys({
				...scope,
				bmOnlyUsers,
			}),
		);
		dispatch(
			fetchGroupJourneys({
				...scope,
				bmOnlyUsers,
			}),
		);
		return action;
	},
);

// Time range
export const setTimeSelection = actionCreator<string>(
	"[DASHBOARD] SET_TIME_SELECTION",
	(action, dispatch, getState) => {
		const state = getState();
		const scope = UserSelectors.selectCurrentScope(state);
		dispatch(
			fetchRootJourneys({
				...scope,
				newTimeRange: action.payload,
			}),
		);
		dispatch(
			fetchGroupJourneys({
				...scope,
				newTimeRange: action.payload,
			}),
		);
		return action;
	},
);

// Date range
export const setDateRangeSelection = actionCreator<{
	range: DateRange | undefined;
	groupIds: string[] | undefined;
}>("[DASHBOARD] SET_DATE_RANGE_SELECTION", (action, dispatch, getState) => {
	const state = getState();
	const scope = UserSelectors.selectCurrentScope(state);

	dispatch(
		fetchRootReports({
			...scope,
			newDateRange: action.payload.range,
			groupIds: action.payload.groupIds,
		}),
	);
	return action;
});

// Group Rank Date range
export const setGroupRankDateRangeSelection = actionCreator<{
	range: DateRange | undefined;
	period: string | undefined;
	groupIds: string[] | undefined;
}>(
	"[DASHBOARD] SET_GROUP_RANK_DATE_RANGE_SELECTION",
	(action, dispatch, getState) => {
		const state = getState();
		const scope = UserSelectors.selectCurrentScope(state);

		dispatch(
			fetchGroupRankingReport({
				...scope,
				newDateRange: action.payload.range,
				groupIds: action.payload.groupIds,
				period: action.payload.period,
			}),
		);
		return action;
	},
);

// Driver/User Rank Date range
export const setUserRankDateRangeSelection = actionCreator<{
	range: DateRange | undefined;
	period: string | undefined;
	groupIds: string[] | undefined;
}>(
	"[DASHBOARD] SET_USER_RANK_DATE_RANGE_SELECTION",
	(action, dispatch, getState) => {
		const state = getState();
		const scope = UserSelectors.selectCurrentScope(state);

		dispatch(
			fetchUserRankingReport({
				...scope,
				newDateRange: action.payload.range,
				groupIds: action.payload.groupIds,
				period: action.payload.period,
			}),
		);
		return action;
	},
);

// User Compliance Date range
export const setComplianceDateRangeSelection = actionCreator<{
	range: DateRange | undefined;
	period: string | undefined;
	groupIds: string[] | undefined;
}>(
	"[DASHBOARD] SET_COMPLIANCE_DATE_RANGE_SELECTION",
	(action, dispatch, getState) => {
		const state = getState();
		const scope = UserSelectors.selectCurrentScope(state);

		dispatch(
			fetchComplianceReport({
				...scope,
				groupIds: action.payload.groupIds,
			}),
		);
		return action;
	},
);

// Bright Coach Group Rank Date range
export const setBrightCoachGroupRankDateRangeSelection = actionCreator<{
	range: DateRange | undefined;
	period: string | undefined;
	groupIds: string[] | undefined;
}>(
	"[DASHBOARD] SET_BRIGHTCOACH_GROUP_RANK_DATE_RANGE_SELECTION",
	(action, dispatch, getState) => {
		const state = getState();
		const scope = UserSelectors.selectCurrentScope(state);
		dispatch(
			fetchBrightCoachGroupRankingReport({
				...scope,
				newDateRange: action.payload.range,
				groupIds: action.payload.groupIds,
				period: action.payload.period,
			}),
		);
		return action;
	},
);

// Bright Coach User Rank Date range
export const setBrightCoachUserRankDateRangeSelection = actionCreator<{
	range: DateRange | undefined;
	period: string | undefined;
	groupIds: string[] | undefined;
}>(
	"[DASHBOARD] SET_BRIGHTCOACH_USER_RANK_DATE_RANGE_SELECTION",
	(action, dispatch, getState) => {
		const state = getState();
		const scope = UserSelectors.selectCurrentScope(state);
		dispatch(
			fetchBrightCoachUserRankingReport({
				...scope,
				newDateRange: action.payload.range,
				groupIds: action.payload.groupIds,
				period: action.payload.period,
			}),
		);
		return action;
	},
);

// Comparison levels
export const setComparisonLevels = actionCreator<string[]>(
	"[DASHBOARD] SET_COMPARISON_LEVELS",
	(action) => {
		if (action.payload.length === 0) {
			action.payload = ["fleet"];
		}
		return action;
	},
);

export const fetchUserCounts = asyncActionCreators<
	ScopeInfo & { appType?: string; vendor?: string },
	FleetUserCounts,
	MetaOf<DashboardReducerState["userCount"]>
>("[DASHBOARD] FETCH_FLEET_USER_COUNTS", async (action, dispatch, getState) => {
	const { fleetId, group, appType, vendor } = action.payload;

	const currentState = selectCurrentScopeUserCount(getState());

	// forced updated with the same group and fleet
	if (
		currentState.value &&
		currentState.meta &&
		!action.type.endsWith("REFRESH")
	) {
		if (
			currentState.meta.fleetId === fleetId &&
			(currentState.meta.group || null) === (group || null)
		) {
			if (currentState.meta.updatedAt > Date.now() - DEFAULT_CACHE_EXPIRE) {
				return currentState.value;
			}
		}
	}

	const res = await Api.fetchFleetUserCounts.submit({
		params: { fleetId: fleetId },
		query: {
			group: group || undefined,
			appType: appType || "software",
			vendor: vendor || "brightmile",
		},
		options: {
			cache: false,
		},
	});
	return res.data;
});
